/**
 * SupportModeBanner — the unmistakable "you are looking at another organization's
 * data as a platform operator" treatment (tenant-foundation Task 7.1 / Req 15.6).
 *
 * WHY THIS EXISTS AT ALL. A support grant is the one situation in the app where
 * someone sees a tenant's data without belonging to that tenant. If that looks
 * identical to ordinary use, two bad things follow: the operator can forget which
 * hat they are wearing and act as though the data were theirs to manage, and nothing
 * on screen reminds them that what they are reading belongs to real people who did
 * not ask them to look. The audit trail records the access; this is what makes it
 * visible in the moment.
 *
 * HOW SUPPORT MODE IS DETECTED, WITHOUT A NEW BACKEND QUERY. The client already
 * knows two things: the active-tenant claim, and (via `useMyMemberships`) the
 * organizations the viewer actually belongs to. An active tenant that is NOT among
 * the viewer's memberships is exactly the support-access shape — a member always has
 * a membership in the tenant they are viewing. No `mySupportGrants` query needed.
 *
 * ERRING TOWARD WARNING. If the claim is merely stale (a membership was removed
 * mid-session), this shows the banner when strictly speaking the session is simply
 * broken. That is the right direction to be wrong in: warning about data that might
 * not be yours costs an operator a moment, whereas silently NOT warning is the
 * failure that matters. It renders nothing until memberships resolve, so an ordinary
 * member never sees a flash of it on load.
 *
 * NOT A SECURITY CONTROL. Read-only is enforced server-side: support grants satisfy
 * `hasSupportReadAccess` only, never the ordinary product-access gate, so no write
 * path can honour one. This banner is the human-facing half of that.
 */

import type { JSX } from 'react';
import { Eye } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';

/** Copy exported so tests assert against one definition. */
export const SUPPORT_MODE_LABEL = 'Operator support view';
export const SUPPORT_MODE_BODY =
  'You are viewing another organization’s data as a platform operator. This access is read-only, time-limited, and recorded.';

export function SupportModeBanner(): JSX.Element | null {
  const { user } = useAuth();
  const { memberships, status } = useMyMemberships();

  const activeTenantId = user?.activeTenantId?.trim();

  // Nothing until we can actually tell — no flash for ordinary members.
  if (status !== 'resolved' || !activeTenantId) return null;

  const belongs = memberships.some((m) => m.tenantId === activeTenantId);
  if (belongs) return null;

  return (
    <div
      data-testid="support-mode-banner"
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 border-b border-border bg-muted px-4 py-2 text-center"
    >
      <Eye className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="text-body-sm font-medium text-foreground">{SUPPORT_MODE_LABEL}</span>
      <span className="text-body-sm text-muted-foreground">{SUPPORT_MODE_BODY}</span>
    </div>
  );
}

export default SupportModeBanner;
